import type { FC } from 'react';
import { useEffect } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router';

import { useAppDispatch } from '@/hooks/redux-hooks';
import { useInitialLoadGate } from '@/providers/InitialLoadGate';
import { storage } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';
import { loginTrip } from '@/slices/tripTokenSlice';

/**
 * Authentication guard layout that protects routes requiring user authentication.
 *
 * - Checks for GX token presence and redirects to login if missing
 * - Initializes Trip token authentication on mount
 * - Saves current pathname before redirect for post-login navigation
 * - Blocks child render until trip token, user config, markets, and dashboards are loaded
 *
 * ExportProvider and SymbolSelector are provided by PublicLayout.
 */
export const AuthLayout: FC = () => {
  const gxToken = useStorageValue('auth.gxToken');
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    if (gxToken) {
      dispatch(loginTrip(gxToken));
    }
  }, [dispatch, gxToken]);

  const isLoading = useInitialLoadGate(!!gxToken);

  if (!gxToken) {
    storage.set('app.lastPathname', location.pathname + location.search);

    return (
      <Navigate
        replace
        to='/login'
      />
    );
  }

  if (isLoading) {
    return null;
  }

  return <Outlet />;
};
